import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { downloadCsv, fileStamp } from '../lib/download';
import { num, toLocalDateString, todayLocal, weekRange } from '../lib/format';
import { useHeldResource, useResource } from '../lib/hooks';

interface Choice {
  id: number;
  name: string;
  active: boolean;
  useType?: string | null;
}
interface Settings {
  roles: Choice[];
  workTypes: Choice[];
  roleIds: number[];
  workTypeIds: number[];
  match: 'all' | 'any';
  configured: boolean;
  updatedBy: string | null;
  updatedUtc: string | null;
}
interface Line {
  roleId?: number | null;
  workTypeId?: number | null;
  role: string;
  workType: string;
  billable: boolean;
  hours: number;
  autotaskBillable: number;
  entries: number;
}
interface Resource {
  id: number;
  name: string;
  active: boolean;
  hours: number;
  billable: number;
  nonBillable: number;
  billablePct: number | null;
  autotaskBillable: number;
  entries: number;
  lines: Line[];
}
/** A name the picker offers: somebody with time in the period, or somebody on
 *  Autotask's roster who has none (`worked: false`) and can still be ticked. */
interface Technician {
  id: number;
  name: string;
  active: boolean;
  hours: number;
  billable: number;
  billablePct: number | null;
  worked: boolean;
}
interface Payload {
  from: string;
  to: string;
  fetchedUtc: string;
  configured: boolean;
  match: 'all' | 'any';
  billableRoles: number;
  billableWorkTypes: number;
  totals: { hours: number; billable: number; nonBillable: number; billablePct: number | null; autotaskBillable: number; entries: number };
  /** The people with time in the period — what the table draws. */
  resources: Resource[];
  /** Everyone who could have logged time — what the picker offers. */
  technicians: Technician[];
  roster: {
    count: number;
    readUtc: string;
    /** Named only when the roster has moved since the API last read it. */
    changes: { joined: string[]; left: string[] } | null;
    unavailable: string | null;
  };
}

const h = (v: number) => num(v, 2);
const pctText = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);
const day = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });

function quick(k: 'week' | 'lastWeek' | 'month' | 'lastMonth' | 'quarter' | 'ytd'): { from: string; to: string } {
  const d = new Date();
  const ld = toLocalDateString;
  switch (k) {
    /* Monday-start, in this browser's timezone. Shared with the Service
       Delivery billable dial so the two never disagree about a week —
       see weekRange in lib/format. */
    case 'week':
      return weekRange('current');
    case 'lastWeek':
      return weekRange('last');
    case 'month':
      return { from: ld(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
    case 'lastMonth':
      return { from: ld(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: ld(new Date(d.getFullYear(), d.getMonth(), 0)) };
    case 'quarter':
      return { from: ld(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)), to: todayLocal() };
    default:
      return { from: ld(new Date(d.getFullYear(), 0, 1)), to: todayLocal() };
  }
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'week', label: 'This week' },
  { k: 'lastWeek', label: 'Last week' },
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'quarter', label: 'This quarter' },
  { k: 'ytd', label: 'This year' },
];

/**
 * Reports › Billable Labor — every hour worked in the dates chosen, by
 * technician, split billable / not by the work roles and work types chosen
 * as billable here (routes/reportsBillableLabor.ts). Autotask's own billable
 * figure is shown beside it for comparison.
 */
export function BillableLaborReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('lastMonth'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [showSettings, setShowSettings] = useState(false);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [open, setOpen] = useState<Set<number>>(new Set());
  /* The work type opened for its entries — for the technicians shown, or one technician. */
  const [drill, setDrill] = useState<WorkDrill | null>(null);
  /* The technician whose tickets are open. */
  const [ticketsFor, setTicketsFor] = useState<{ id: number; name: string } | null>(null);
  /* The technicians to report on: null is everyone. Remembered in this browser. */
  const [picked, setPicked] = useState<number[] | null>(() => {
    try {
      const v = localStorage.getItem(PICK_KEY);
      return v ? (JSON.parse(v) as number[]) : null;
    } catch {
      return null;
    }
  });
  const choose = (next: number[] | null) => {
    setPicked(next);
    try {
      if (next === null) localStorage.removeItem(PICK_KEY);
      else localStorage.setItem(PICK_KEY, JSON.stringify(next));
    } catch {
      /* not remembered; still applied */
    }
  };
  const settings = useResource<Settings>('/reports/billable-labor/settings');
  const data = useHeldResource<Payload>(`/reports/billable-labor?${new URLSearchParams({ from, to, v: settings.data?.updatedUtc ?? '' })}`);
  const d = data.data;

  useEffect(() => {
    if (settings.data && !settings.data.configured) setShowSettings(true);
  }, [settings.data]);

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const only = picked === null ? null : new Set(picked);
    return (d?.resources ?? []).filter(
      (r) => (!only || only.has(r.id)) && (!activeOnly || r.active) && (!q || r.name.toLowerCase().includes(q)),
    );
  }, [d, search, activeOnly, picked]);
  const sum = (k: 'hours' | 'billable' | 'nonBillable' | 'autotaskBillable') => rows.reduce((t, r) => t + r[k], 0);
  /* Each work type across the technicians shown. */
  const byType = useMemo(() => {
    const m = new Map<string, { workTypeId: number | null; workType: string; hours: number; billable: number; autotaskBillable: number; entries: number; people: Set<number> }>();
    for (const r of rows) {
      for (const l of r.lines) {
        const k = String(l.workTypeId ?? 'none');
        const e = m.get(k) ?? { workTypeId: l.workTypeId ?? null, workType: l.workType, hours: 0, billable: 0, autotaskBillable: 0, entries: 0, people: new Set<number>() };
        e.hours += l.hours;
        if (l.billable) e.billable += l.hours;
        e.autotaskBillable += l.autotaskBillable;
        e.entries += l.entries;
        e.people.add(r.id);
        m.set(k, e);
      }
    }
    return [...m.values()].sort((a, b) => b.hours - a.hours);
  }, [rows]);
  const shownPct = sum('hours') ? Math.round((sum('billable') / sum('hours')) * 1000) / 10 : null;
  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const exportCsv = () =>
    downloadCsv(`billable_labor_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Technician', 'Hours worked', 'Billable hours', 'Non-billable hours', 'Billable %', 'Autotask billable hours'],
      ...rows.map((r) => [r.name, r.hours, r.billable, r.nonBillable, r.billablePct ?? '', r.autotaskBillable]),
      [],
      ['Technician', 'Role', 'Work type', 'Counts as billable', 'Hours', 'Autotask billable hours', 'Entries'],
      ...rows.flatMap((r) => r.lines.map((l) => [r.name, l.role, l.workType, l.billable ? 'Yes' : 'No', l.hours, l.autotaskBillable, l.entries])),
    ]);

  return (
    <>
      <PageHead
        title="Billable Labor"
        lede="Every hour worked, by technician, split into billable and not by the work roles and work types you choose — with Autotask's own billable hours beside it."
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => setShowSettings((s) => !s)}>
            {showSettings ? 'Hide billable rules' : 'Choose what is billable'}
          </button>
        }
      />

      {settings.error && <ErrorBanner message={settings.error} />}
      {showSettings && settings.data && (
        <SettingsPanel
          settings={settings.data}
          onSaved={() => {
            settings.reload();
            setShowSettings(false);
          }}
        />
      )}

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Date worked from">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((q) => (
                <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(q.k))}>
                  {q.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-3" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Technicians to report on">
              <TechnicianPicker people={d?.technicians ?? []} picked={picked} onChange={choose} />
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name" />
            </Field>
            <label className="checkbox">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
              Active technicians only
            </label>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          {d && (
            <p className="xs muted mt-2">
              {day(d.from)} – {day(d.to)} · {num(d.totals.entries)} time entries. Billable = an hour whose{' '}
              {d.match === 'all' ? 'work role AND work type are both' : 'work role OR work type is'} ticked as billable ({d.billableRoles} roles,{' '}
              {d.billableWorkTypes} work types).
            </p>
          )}
          {/* Who Autotask says the technicians are, read each time this report
              is. The roster is the picker's list, so saying how many there are
              and when they were read is how you tell a missing name from a
              stale one. */}
          {/* Guarded rather than assumed: during a rolling deploy a new page
              can be served by an API that does not send this yet. */}
          {d?.roster && <RosterLine roster={d.roster} />}
        </div>
      </section>

      {d && !d.configured && (
        <div className="card mb-4">
          <div className="card-body small">
            <strong>Nothing is ticked as billable yet,</strong> so every hour shows as non-billable. Choose the billable work roles and work types above.
          </div>
        </div>
      )}
      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading time entries from Autotask" />}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Hours worked" value={h(sum('hours'))} foot={`${num(rows.length)} technicians`} />
            <Stat label="Billable" value={h(sum('billable'))} foot={`${pctText(shownPct)} of hours worked`} />
            <Stat label="Non-billable" value={h(sum('nonBillable'))} foot="by your rules" />
            <Stat label="Autotask billable" value={h(sum('autotaskBillable'))} foot="hours to bill, not marked non-billable" />
          </div>
          <section className="card">
            <div className="table-wrap">
              <table className="table bl-table" data-nosort>
                <thead>
                  <tr>
                    <th>Technician</th>
                    <th className="num">Hours worked</th>
                    <th className="num">Billable</th>
                    <th className="num">Non-billable</th>
                    <th style={{ width: 200 }}>Billable %</th>
                    <th className="num">Autotask billable</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted small">
                        {picked !== null && picked.length === 0 ? 'No technicians chosen — pick some under Technicians to report on.' : 'No time in these dates for the technicians chosen.'}
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => {
                    const isOpen = open.has(r.id);
                    return (
                      <Fragment key={r.id}>
                        <tr className={isOpen ? 'is-open' : undefined}>
                          <td>
                            <button type="button" className="link-button" onClick={() => toggle(r.id)} aria-expanded={isOpen}>
                              {isOpen ? '▾' : '▸'} {r.name}
                            </button>
                            {!r.active && <span className="xs muted"> · inactive</span>}
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm bl-tickets-btn"
                              onClick={() => setTicketsFor({ id: r.id, name: r.name })}
                              title="The tickets they logged time on"
                            >
                              Tickets
                            </button>
                          </td>
                          <td className="num">{h(r.hours)}</td>
                          <td className="num strong">{h(r.billable)}</td>
                          <td className="num">{h(r.nonBillable)}</td>
                          <td>
                            <span className="bl-bar">
                              <span className="bl-bar-track">
                                <span className="bl-bar-fill" style={{ width: `${Math.min(100, r.billablePct ?? 0)}%` }} />
                              </span>
                              <span className="mono xs">{pctText(r.billablePct)}</span>
                            </span>
                          </td>
                          <td className="num">{h(r.autotaskBillable)}</td>
                        </tr>
                        {isOpen &&
                          r.lines.map((l) => (
                            <tr key={`${l.role}|${l.workType}`} className="bl-line">
                              <td className="xs">
                                {l.role} ·{' '}
                                <button
                                  type="button"
                                  className="link-button"
                                  title="Open the time entries"
                                  onClick={() =>
                                    setDrill({
                                      workTypeId: l.workTypeId ?? null,
                                      roleId: l.roleId ?? null,
                                      byRole: true,
                                      resourceIds: [r.id],
                                      title: `${l.workType} — ${r.name}`,
                                      subtitle: `${l.role} · ${l.billable ? 'counts as billable' : 'not billable'}`,
                                    })
                                  }
                                >
                                  {l.workType}
                                </button>{' '}
                                <span className={`badge ${l.billable ? 'badge-good' : 'badge-neutral'}`}>{l.billable ? 'billable' : 'not billable'}</span>
                              </td>
                              <td className="num xs">{h(l.hours)}</td>
                              <td className="num xs">{l.billable ? h(l.hours) : '—'}</td>
                              <td className="num xs">{l.billable ? '—' : h(l.hours)}</td>
                              <td className="xs muted">{num(l.entries)} entries</td>
                              <td className="num xs">{h(l.autotaskBillable)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                  {rows.length > 1 && (
                    <tr className="cr-total">
                      <td>{num(rows.length)} technicians</td>
                      <td className="num">{h(sum('hours'))}</td>
                      <td className="num">{h(sum('billable'))}</td>
                      <td className="num">{h(sum('nonBillable'))}</td>
                      <td className="mono xs">{pctText(shownPct)}</td>
                      <td className="num">{h(sum('autotaskBillable'))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {byType.length > 0 && (
            <section className="card mt-4">
              <div className="card-head">
                <div className="grow">
                  <h3>By work type</h3>
                  <p className="sub">Across the technicians shown. Click a work type for its time entries.</p>
                </div>
              </div>
              <div className="table-wrap">
                <table className="table" data-nosort>
                  <thead>
                    <tr>
                      <th>Work type</th>
                      <th className="num">Hours worked</th>
                      <th className="num">Billable</th>
                      <th className="num">Non-billable</th>
                      <th className="num">Technicians</th>
                      <th className="num">Entries</th>
                      <th className="num">Autotask billable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byType.map((w) => (
                      <tr key={String(w.workTypeId ?? 'none')}>
                        <td>
                          <button
                            type="button"
                            className="link-button"
                            onClick={() =>
                              setDrill({
                                workTypeId: w.workTypeId,
                                resourceIds: [...w.people],
                                title: w.workType,
                                subtitle: `${num(w.people.size)} technician${w.people.size === 1 ? '' : 's'}`,
                              })
                            }
                          >
                            {w.workType}
                          </button>
                        </td>
                        <td className="num">{h(w.hours)}</td>
                        <td className="num strong">{h(w.billable)}</td>
                        <td className="num">{h(w.hours - w.billable)}</td>
                        <td className="num">{num(w.people.size)}</td>
                        <td className="num">{num(w.entries)}</td>
                        <td className="num">{h(w.autotaskBillable)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {drill && <WorkTypeDrawer drill={drill} from={from} to={to} onClose={() => setDrill(null)} />}
      {ticketsFor && <TicketsDrawer who={ticketsFor} from={from} to={to} onClose={() => setTicketsFor(null)} />}
    </>
  );
}

interface WorkDrill {
  workTypeId: number | null;
  /** Narrow to one role too — from a technician's role and work type line. */
  byRole?: boolean;
  roleId?: number | null;
  resourceIds: number[];
  title: string;
  subtitle: string;
}
interface EntryRow {
  id: number;
  date: string | null;
  technician: string;
  role: string;
  workType: string;
  on: string;
  client: string | null;
  hours: number;
  autotaskBillable: number;
  billable: boolean;
  notes: string | null;
}

/** The time entries behind a work type (and, from a technician's line, a role). */
function WorkTypeDrawer({ drill, from, to, onClose }: { drill: WorkDrill; from: string; to: string; onClose: () => void }) {
  const params = new URLSearchParams({ from, to, workTypeId: drill.workTypeId === null ? 'none' : String(drill.workTypeId) });
  if (drill.byRole) params.set('roleId', drill.roleId === null || drill.roleId === undefined ? 'none' : String(drill.roleId));
  if (drill.resourceIds.length) params.set('resourceIds', drill.resourceIds.join(','));
  const data = useResource<{ rows: EntryRow[] }>(`/reports/billable-labor/entries?${params}`);
  const rows = data.data?.rows ?? [];
  const total = rows.reduce((t, r) => t + r.hours, 0);
  const billable = rows.filter((r) => r.billable).reduce((t, r) => t + r.hours, 0);
  const exportCsv = () =>
    downloadCsv(`billable_labor_${drill.title.replace(/[^A-Za-z0-9]+/g, '_')}_${fileStamp()}.csv`, [
      ['Date', 'Technician', 'Role', 'Work type', 'Ticket / task', 'Client', 'Hours', 'Counts as billable', 'Autotask billable hours', 'Notes'],
      ...rows.map((r) => [r.date, r.technician, r.role, r.workType, r.on, r.client ?? '', r.hours, r.billable ? 'Yes' : 'No', r.autotaskBillable, r.notes ?? '']),
    ]);
  return (
    <Drawer
      open
      onClose={onClose}
      wide
      title={drill.title}
      subtitle={`${drill.subtitle} · ${day(from)} – ${day(to)}`}
      footer={
        <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
          Export CSV
        </button>
      }
    >
      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading the time entries" />}
      {data.data && (
        <>
          <div className="grid grid-3 mb-4">
            <Stat label="Hours worked" value={h(total)} foot={`${num(rows.length)} entries`} />
            <Stat label="Billable" value={h(billable)} foot="by your rules" />
            <Stat label="Autotask billable" value={h(rows.reduce((t, r) => t + r.autotaskBillable, 0))} foot="hours to bill" />
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Technician</th>
                  <th>Role</th>
                  <th>Ticket / task</th>
                  <th>Client</th>
                  <th className="num">Hours</th>
                  <th>Billable</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="xs" style={{ whiteSpace: 'nowrap' }}>{r.date ? day(r.date) : '—'}</td>
                    <td className="xs">{r.technician}</td>
                    <td className="xs">{r.role}</td>
                    <td className="xs">{r.on}</td>
                    <td className="xs">{r.client ?? '—'}</td>
                    <td className="num">{h(r.hours)}</td>
                    <td className="xs">{r.billable ? 'Yes' : 'No'}</td>
                    <td className="xs muted bl-notes" title={r.notes ?? ''}>{r.notes ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Drawer>
  );
}

interface TicketRow {
  id: number;
  number: string;
  title: string;
  client: string | null;
  status: string | null;
  issueType: string | null;
  link: string | null;
  hours: number;
  billable: number;
  autotaskBillable: number;
  workTypes: string[];
  entries: { id: number; date: string | null; role: string; workType: string; hours: number; billable: boolean; autotaskBillable: number; notes: string | null }[];
}

/** One technician's tickets in the period, each opening to its time entries. */
function TicketsDrawer({ who, from, to, onClose }: { who: { id: number; name: string }; from: string; to: string; onClose: () => void }) {
  const data = useResource<{ tickets: TicketRow[]; other: { projectHours: number; projectBillable: number; otherHours: number; otherBillable: number } }>(
    `/reports/billable-labor/tickets?${new URLSearchParams({ from, to, resourceId: String(who.id) })}`,
  );
  const [openT, setOpenT] = useState<Set<number>>(new Set());
  const [q, setQ] = useState('');
  const all = data.data?.tickets ?? [];
  const rows = all.filter((t) => !q.trim() || [t.number, t.title, t.client].some((v) => v && v.toLowerCase().includes(q.trim().toLowerCase())));
  const o = data.data?.other;
  const toggleT = (id: number) =>
    setOpenT((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const exportCsv = () =>
    downloadCsv(`${who.name.replace(/[^A-Za-z0-9]+/g, '_')}_tickets_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Ticket', 'Title', 'Client', 'Status', 'Issue type', 'Hours', 'Billable hours', 'Autotask billable hours', 'Work types'],
      ...rows.map((t) => [t.number, t.title, t.client ?? '', t.status ?? '', t.issueType ?? '', t.hours, t.billable, t.autotaskBillable, t.workTypes.join('; ')]),
      [],
      ['Ticket', 'Date', 'Role', 'Work type', 'Hours', 'Counts as billable', 'Autotask billable hours', 'Notes'],
      ...rows.flatMap((t) => t.entries.map((e) => [t.number, e.date, e.role, e.workType, e.hours, e.billable ? 'Yes' : 'No', e.autotaskBillable, e.notes ?? ''])),
    ]);
  return (
    <Drawer
      open
      onClose={onClose}
      wide
      title={`${who.name} — tickets`}
      subtitle={`${day(from)} – ${day(to)}`}
      footer={
        <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
          Export CSV
        </button>
      }
    >
      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && <Loading label="Reading their tickets" />}
      {data.data && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Tickets" value={num(all.length)} foot="with time logged" />
            <Stat label="Ticket hours" value={h(all.reduce((t, x) => t + x.hours, 0))} foot={`${h(all.reduce((t, x) => t + x.billable, 0))} billable`} />
            <Stat label="Project tasks" value={h(o?.projectHours ?? 0)} foot={`${h(o?.projectBillable ?? 0)} billable`} />
            <Stat label="Other time" value={h(o?.otherHours ?? 0)} foot={`${h(o?.otherBillable ?? 0)} billable`} />
          </div>
          <input className="input mb-2" style={{ maxWidth: 320 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticket, title or client" />
          <div className="table-wrap">
            <table className="table bl-table" data-nosort>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th className="num">Hours</th>
                  <th className="num">Billable</th>
                  <th className="num">Autotask billable</th>
                  <th>Work types</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted small">
                      No ticket time in these dates.
                    </td>
                  </tr>
                )}
                {rows.map((t) => {
                  const isOpen = openT.has(t.id);
                  return (
                    <Fragment key={t.id}>
                      <tr className={isOpen ? 'is-open' : undefined}>
                        <td>
                          <button type="button" className="link-button" onClick={() => toggleT(t.id)} aria-expanded={isOpen}>
                            {isOpen ? '▾' : '▸'} <span className="mono xs">{t.number}</span>
                          </button>{' '}
                          {t.link && (
                            <a href={t.link} target="_blank" rel="noreferrer" className="xs" title="Open in Autotask">
                              ↗
                            </a>
                          )}
                          <div className="xs muted">{t.title}</div>
                        </td>
                        <td className="xs">{t.client ?? '—'}</td>
                        <td className="xs">
                          {t.status ?? '—'}
                          {t.issueType && <div className="muted">{t.issueType}</div>}
                        </td>
                        <td className="num">{h(t.hours)}</td>
                        <td className="num strong">{h(t.billable)}</td>
                        <td className="num">{h(t.autotaskBillable)}</td>
                        <td className="xs">{t.workTypes.join(', ')}</td>
                      </tr>
                      {isOpen &&
                        t.entries.map((e) => (
                          <tr key={e.id} className="bl-line">
                            <td className="xs">{e.date ? day(e.date) : '—'}</td>
                            <td className="xs" colSpan={2}>
                              {e.role} · {e.workType}
                              {e.notes && <div className="muted bl-notes" title={e.notes}>{e.notes}</div>}
                            </td>
                            <td className="num xs">{h(e.hours)}</td>
                            <td className="num xs">{e.billable ? h(e.hours) : '—'}</td>
                            <td className="num xs">{h(e.autotaskBillable)}</td>
                            <td className="xs">{e.billable ? 'billable' : 'not billable'}</td>
                          </tr>
                        ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Drawer>
  );
}

const PICK_KEY = 'billable-labor-technicians';

/**
 * Choose which technicians the report covers: a button that opens a
 * checklist (search, tick all, clear). Technicians with no time in the dates
 * shown are not listed; a pick survives a change of dates, so someone chosen
 * reappears when they have time again.
 */
/**
 * What Autotask's roster said this time, and whether it has moved.
 *
 * The count is here because this picker's list is now read rather than
 * inferred: if a name is missing, the number tells you at a glance whether
 * the roster came back at all. The joined / left line answers "check the
 * employees haven't changed" — it names who has appeared or gone since the
 * API last looked, and says nothing at all when nothing has moved, so it is
 * a signal rather than another line of furniture.
 */
function RosterLine({ roster }: { roster: Payload['roster'] }) {
  const at = new Date(roster.readUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (roster.unavailable) {
    return (
      <p className="xs muted mt-1">
        Autotask's technician roster could not be read, so this list is only the people with time in these dates — a new
        starter will not be in it. {roster.unavailable}
      </p>
    );
  }
  return (
    <p className="xs muted mt-1">
      {num(roster.count)} technicians on Autotask's roster, read at {at}.
      {roster.changes && (
        <>
          {' '}
          <strong>
            Since the last read:
            {roster.changes.joined.length > 0 && ` joined — ${roster.changes.joined.join(', ')}.`}
            {roster.changes.left.length > 0 && ` no longer listed — ${roster.changes.left.join(', ')}.`}
          </strong>
        </>
      )}
    </p>
  );
}

function TechnicianPicker({
  people,
  picked,
  onChange,
}: {
  people: Technician[];
  picked: number[] | null;
  onChange: (next: number[] | null) => void;
}) {
  const [openList, setOpenList] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!openList) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpenList(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openList]);

  const sorted = [...people].sort((a, b) => a.name.localeCompare(b.name));
  const shown = sorted.filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const chosen = new Set(picked ?? people.map((p) => p.id));
  const inView = people.filter((p) => chosen.has(p.id)).length;
  const label = picked === null ? 'All technicians' : picked.length === 0 ? 'None chosen' : `${inView} of ${people.length} technicians`;
  const set = (ids: number[], on: boolean) => {
    const n = new Set(picked ?? people.map((p) => p.id));
    for (const id of ids) {
      if (on) n.add(id);
      else n.delete(id);
    }
    onChange(people.every((p) => n.has(p.id)) && picked === null ? null : [...n]);
  };

  return (
    <div className="bl-pick" ref={box}>
      <button type="button" className="select bl-pick-btn" onClick={() => setOpenList((o) => !o)} aria-expanded={openList}>
        {label}
      </button>
      {openList && (
        <div className="bl-pick-panel">
          <input className="input input-sm" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search technicians" />
          <div className="row wrap mt-2" style={{ gap: 4 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null)}>
              Everyone
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(shown.map((p) => p.id), true)}>
              Tick shown
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(shown.map((p) => p.id), false)}>
              Clear shown
            </button>
          </div>
          <div className="bl-items mt-2">
            {shown.length === 0 && <p className="xs muted">No technicians match.</p>}
            {shown.map((p) => (
              <label key={p.id} className="checkbox bl-item">
                <input type="checkbox" checked={chosen.has(p.id)} onChange={(e) => set([p.id], e.target.checked)} />
                <span>
                  {p.name}
                  {!p.active && <span className="xs muted"> · inactive</span>}
                  {/* Somebody on the roster with nothing logged in these
                      dates — a new hire, or a week off. Tickable all the
                      same, which is the point of listing them. */}
                  <span className="xs muted"> · {p.worked ? `${num(p.hours, 1)} h` : 'no time in these dates'}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="row-between mt-2">
            <span className="xs muted">Remembered in this browser.</span>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpenList(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Tick the work roles and work types that count as billable, and how they combine. */
function SettingsPanel({ settings, onSaved }: { settings: Settings; onSaved: () => void }) {
  const [roles, setRoles] = useState<Set<number>>(new Set(settings.roleIds));
  const [types, setTypes] = useState<Set<number>>(new Set(settings.workTypeIds));
  const [match, setMatch] = useState<'all' | 'any'>(settings.match);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put('/reports/billable-labor/settings', { roleIds: [...roles], workTypeIds: [...types], match });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setBusy(false);
    }
  };

  return (
    <section className="card mb-4">
      <div className="card-head">
        <div className="grow">
          <h3>What counts as billable</h3>
          <p className="sub">
            Tick the work roles and work types that are billable. Saved for everyone.
            {settings.updatedUtc &&
              ` Last changed ${new Date(settings.updatedUtc).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${settings.updatedBy ? ` by ${settings.updatedBy}` : ''}.`}
          </p>
        </div>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="card-body">
        {error && <ErrorBanner message={error} />}
        <div className="row wrap mb-3" style={{ gap: 'var(--space-4)' }}>
          <span className="small strong">An hour is billable when</span>
          <label className="checkbox">
            <input type="radio" name="bl-match" checked={match === 'all'} onChange={() => setMatch('all')} />
            its work role <strong>and</strong> its work type are both ticked
          </label>
          <label className="checkbox">
            <input type="radio" name="bl-match" checked={match === 'any'} onChange={() => setMatch('any')} />
            its work role <strong>or</strong> its work type is ticked
          </label>
        </div>
        <div className="bl-choose">
          <ChoiceList title="Work roles" items={settings.roles} chosen={roles} onChange={setRoles} />
          <ChoiceList title="Work types" items={settings.workTypes} chosen={types} onChange={setTypes} />
        </div>
      </div>
    </section>
  );
}

function ChoiceList({
  title,
  items,
  chosen,
  onChange,
}: {
  title: string;
  items: Choice[];
  chosen: Set<number>;
  onChange: (next: Set<number>) => void;
}) {
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const shown = items.filter((i) => (showInactive || i.active || chosen.has(i.id)) && (!q.trim() || i.name.toLowerCase().includes(q.trim().toLowerCase())));
  const set = (ids: number[], on: boolean) => {
    const n = new Set(chosen);
    for (const id of ids) {
      if (on) n.add(id);
      else n.delete(id);
    }
    onChange(n);
  };
  return (
    <div className="bl-list">
      <div className="row-between wrap mb-2">
        <span className="small strong">
          {title} <span className="xs muted">({chosen.size} billable of {items.length})</span>
        </span>
        <span className="row" style={{ gap: 4 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(shown.map((i) => i.id), true)}>
            Tick shown
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(shown.map((i) => i.id), false)}>
            Clear shown
          </button>
        </span>
      </div>
      <div className="row wrap mb-2" style={{ gap: 'var(--space-2)' }}>
        <input className="input input-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${title.toLowerCase()}`} />
        <label className="checkbox xs">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>
      <div className="bl-items">
        {shown.length === 0 && <p className="xs muted">None match.</p>}
        {shown.map((i) => (
          <label key={i.id} className="checkbox bl-item">
            <input type="checkbox" checked={chosen.has(i.id)} onChange={(e) => set([i.id], e.target.checked)} />
            <span>
              {i.name}
              {!i.active && <span className="xs muted"> · inactive</span>}
              {i.useType && <span className="xs muted"> · {i.useType}</span>}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
